import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { showPageFlash } from "../../../src/application/flash_messages.js"

export default class extends Controller {
  static targets = ["manageModal", "addContentModal", "nameInput", "saveButton"]

  static values = {
    createUrl: String,
    documentsPath: String
  }

  connect() {
    this.todoListId = null
    this.syncSaveButton()
  }

  openManageModal(event) {
    event?.preventDefault()
    this.resetManageForm()
    this.manageModalController()?.show()
    requestAnimationFrame(() => this.nameInputTarget?.focus())
  }

  skip(event) {
    event.preventDefault()
    this.manageModalController()?.close()
    Turbo.visit(this.documentsPathValue)
  }

  async save(event) {
    event.preventDefault()

    const title = this.nameInputTarget?.value.trim()
    if (!title || this.saveButtonTarget?.disabled) return

    this.saveButtonTarget.disabled = true

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: this.requestHeaders(),
        body: JSON.stringify({ title }),
        redirect: "manual"
      })

      if (response.type === "opaqueredirect" || (response.status >= 300 && response.status < 400)) {
        throw new Error("Unable to create to-do list")
      }

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.error || "Unable to create to-do list")
      }

      this.todoListId = payload.todo_list_id
      this.manageModalController()?.close()
      this.addContentModalController()?.show()
    } catch (error) {
      showPageFlash(error.message || "Unable to create to-do list")
      this.saveButtonTarget.disabled = false
      this.syncSaveButton()
    }
  }

  goToPatientLibrary(event) {
    event.preventDefault()
    if (!this.todoListId) return

    this.addContentModalController()?.close()
    Turbo.visit(this.documentsPathValue)
  }

  syncSaveButton() {
    if (!this.hasSaveButtonTarget) return

    const hasName = Boolean(this.nameInputTarget?.value.trim())
    this.saveButtonTarget.disabled = !hasName
  }

  handleNameInputKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      if (!this.saveButtonTarget?.disabled) this.save(event)
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      this.manageModalController()?.close()
    }
  }

  resetManageForm() {
    if (this.hasNameInputTarget) {
      this.nameInputTarget.value = ""
    }
    this.syncSaveButton()
  }

  requestHeaders() {
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": this.csrfToken()
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  manageModalController() {
    return this.modalControllerFor(this.manageModalTarget)
  }

  addContentModalController() {
    return this.modalControllerFor(this.addContentModalTarget)
  }

  modalControllerFor(element) {
    if (!element) return null

    return this.application.getControllerForElementAndIdentifier(element, "railsui-modal")
  }
}
